/**
 * Pure helpers behind the user-chosen order of sidebar links and tabs
 * (``useReorderable``). Kept apart from the hook so the merge rules can be
 * tested without React or a DOM.
 */

export type DropPosition = 'before' | 'after'

/**
 * Parses what was saved in localStorage. Anything that isn't a list of
 * strings — corrupt JSON, an older shape — counts as "no saved order".
 */
export function parseStoredOrder(raw: string | null): string[] | null {
  if (raw === null) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (Array.isArray(parsed) && parsed.every((v) => typeof v === 'string')) {
      return parsed
    }
  } catch {
    // corrupt JSON — fall back to the default order
  }
  return null
}

/**
 * Applies a saved order to the items that exist today. Saved ids that no
 * longer exist are dropped; items the saved order doesn't know (a link added
 * in a later release) land right after their predecessor in the default
 * order, so they show up where the code put them instead of at the end.
 */
export function applyStoredOrder(
  ids: readonly string[],
  stored: readonly string[] | null,
): string[] {
  if (!stored) return [...ids]
  const known = new Set(ids)
  const result = [...new Set(stored)].filter((id) => known.has(id))
  const placed = new Set(result)
  ids.forEach((id, i) => {
    if (placed.has(id)) return
    const prev = i > 0 ? ids[i - 1] : undefined
    const at = prev === undefined ? 0 : result.indexOf(prev) + 1
    result.splice(at, 0, id)
    placed.add(id)
  })
  return result
}

/** Moves ``dragged`` to just before or after ``target``. */
export function moveItem(
  order: readonly string[],
  dragged: string,
  target: string,
  position: DropPosition,
): string[] {
  if (dragged === target || !order.includes(dragged)) return [...order]
  const result = order.filter((id) => id !== dragged)
  const at = result.indexOf(target)
  if (at === -1) return [...order]
  result.splice(position === 'before' ? at : at + 1, 0, dragged)
  return result
}

export function sameOrder(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i])
}
